import { useMutation, useQuery } from 'urql';
import { graphql } from '../../gql';
import { Link } from '@tanstack/react-router';
import React, { useState } from 'react';
import {
  Grid,
  GridColumn,
  GridNoRecords,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { HeroIcon } from '../../shared/HeroIcon.tsx';
import { PageTitle } from '../PageTitle.tsx';

interface CompanySitesProps {
  companyId: string;
}

const CompanySitesQuery = graphql(/* GraphQL */ `
  query companySites($companyId: String!) {
    companySites(companyId: $companyId) {
      title
      slug
      draft
      externalUrl
    }
  }
`);

const DeleteComanySiteMutation = graphql(/* GraphQL */ `
  mutation deleteCompanySite($companyId: String!, $slug: String!) {
    deleteCompanySite(companyId: $companyId, slug: $slug)
  }
`);

export const CompanySitesPage: React.FC<CompanySitesProps> = ({
  companyId,
}) => {
  // TODO: Error handling
  const [{ data, fetching }, reExecuteCompanySitesQuery] = useQuery({
    query: CompanySitesQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const [, deleteCompanySite] = useMutation(DeleteComanySiteMutation);

  const [sort, setSort] = useState<SortDescriptor[]>([]);

  const deleteSite = (slug: string) => {
    deleteCompanySite({ companyId, slug }).then(() =>
      reExecuteCompanySitesQuery(),
    );
  };

  return (
    <>
      <PageTitle title="Seiten" />

      <div className={'mb-4 flex justify-end'}>
        <Link
          className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
          to={'/admin/sites/new'}
        >
          <HeroIcon name="DocumentPlus" className="block h-5 w-5" />
          Neue Seite
        </Link>
      </div>

      <Grid
        className="h-[653px] [&_.k-grid-norecords-template]:border-0"
        data={orderBy(data?.companySites ?? [], sort)}
        pageable
        sortable
        sort={sort}
        onSortChange={(e: GridSortChangeEvent) => setSort(e.sort)}
        showLoader={fetching}
      >
        <GridNoRecords>{fetching ? '' : 'Keine Seiten gefunden'}</GridNoRecords>
        <GridColumn field="title" title="Titel" />
        <GridColumn field="slug" title="Navigation Titel" />
        <GridColumn field="draft" title="Entwurf" />
        <GridColumn field="externalUrl" title="URL" />
        <GridColumn
          width={'120px'}
          sortable={false}
          cells={{
            data: (props) => (
              <td className={'k-button-link-secondary flex space-x-2'}>
                <Link
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  to={`/admin/sites/edit/${props.dataItem.slug}` as any}
                  className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md !text-[#6c757d] hover:!text-white"
                  title="Seite bearbeiten"
                >
                  <HeroIcon name="Pencil" className="block h-5 w-5" />
                </Link>
                <button
                  className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md"
                  onClick={() => deleteSite(props.dataItem.slug)}
                  title="Seite löschen"
                >
                  <HeroIcon name="Trash" className="block h-5 w-5" />
                </button>
              </td>
            ),
          }}
        />
      </Grid>
    </>
  );
};
